/* Rhino Metals Product Configurator — data-driven engine.
   Reads option_group/option_value/model/model_size_configuration metaobject data
   serialized by product-configurator-rhino.liquid. Step order, compatibility, and
   pricing all come from that data — nothing here is specific to any one series. */
(function () {
  'use strict';

  function money(n) {
    return '$' + Math.abs(Number(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  // A value's modifier (flat or price_by_size, see effectiveModifier) can be negative — a real
  // discount relative to the group's base price, e.g. Magnum's Marble finishes. Un-signed money()
  // plus a sign picked here keeps a discount readable ("- $730.00") instead of "+ $-730.00" (money()
  // rendering the negative number's own minus sign right after the unconditional "+ " prefix).
  function delta(n) {
    return n ? (n < 0 ? '- ' : '+ ') + money(n) : 'Included';
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  // Resolves once the image is loaded (or fails) so a hero/sticky-cart layer swap never
  // reveals a half-loaded stack. Resolving on error too keeps a bad URL from hanging the swap.
  function preloadImage(url) {
    if (!url) return Promise.resolve();
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = img.onerror = () => resolve();
      img.src = url;
    });
  }

  function init(root) {
    // Key Features grid (server-rendered <i data-lucide="..."> icons) needs the same
    // library + call as safe-builder.js to actually render as SVGs. Called unconditionally,
    // ahead of the data.model early-return below, since the icons are static markup that
    // exists regardless of whether the configurator itself has data.
    if (typeof lucide !== 'undefined') lucide.createIcons();

    const sectionId = root.dataset.sectionId;
    const dataEl = document.getElementById('rc-data-' + sectionId);
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Rhino configurator: failed to parse data', e);
      return;
    }

    const $ = (id) => document.getElementById(id + '-' + sectionId);
    const stepsEl = $('rc-steps');
    const priceTopEl = $('rc-price-top');
    const priceTotalEl = $('rc-price-total');
    const priceNoteEl = $('rc-price-note');
    const configStringEl = $('rc-config-string');
    const derivedEl = $('rc-derived');
    const includedEl = $('rc-included');
    const ctaEl = $('rc-cta');
    const ctaHelperEl = $('rc-cta-helper');
    const heroEl = $('rc-hero');
    const heroHingeEl = $('rc-hero-hinge');
    const heroHardwareEl = $('rc-hero-hardware');
    const frameEl = heroEl ? heroEl.closest('.rc__frame') : null;
    const stickyCartEl = $('rc-sticky-cart');
    const stickyCartToggleEl = $('rc-sticky-cart-toggle');
    const stickyCartImageEl = $('rc-sticky-cart-image');
    const stickyCartHingeEl = $('rc-sticky-cart-image-hinge');
    const stickyCartHardwareEl = $('rc-sticky-cart-image-hardware');
    const stickyCartVariantEl = $('rc-sticky-cart-variant');
    const thumbConfiguredBtn = root.querySelector('[data-rc-thumb-configured]');
    const thumbConfiguredBody = $('rc-thumb-configured-body');
    const thumbConfiguredHinge = $('rc-thumb-configured-hinge');
    const thumbConfiguredHardware = $('rc-thumb-configured-hardware');
    const stickyCartPriceEl = $('rc-sticky-cart-price');
    const stickyCartJumpEl = $('rc-sticky-cart-jump');
    const stickyCartAddEl = $('rc-sticky-cart-add');
    const stickyCartAddLabelEl = $('rc-sticky-cart-add-label');
    const specCapacityEl = $('rc-spec-capacity');
    const specWeightEl = $('rc-spec-weight');
    // Bumped on every hero image swap so a slower-loading, now-superseded swap can tell
    // (once its preloads resolve) that a newer selection already won and skip applying.
    let heroSwapToken = 0;

    if (!data.model) {
      if (ctaEl) {
        ctaEl.disabled = true;
        ctaEl.textContent = 'Configurator unavailable';
      }
      if (stickyCartAddEl) stickyCartAddEl.disabled = true;
      if (stickyCartAddLabelEl) stickyCartAddLabelEl.textContent = 'Configurator unavailable';
      return;
    }

    const sizeConfigs = (data.modelSizeConfigurations || []).filter(Boolean);
    const configuredImages = (data.configuredImages || []).filter(Boolean);
    // "body" layer carries the recolored/retextured safe body (no hardware); "hinge" layer is a
    // small color-matched overlay showing the hinge + cap hardware for one specific body color +
    // hinge side (e.g. Beast Vault Door's bvd_body_{color}-{hinge}-{caps} photos — despite the
    // "body" substring in their filename, these are NOT alternate body photos, they're a hinge
    // overlay keyed to the plain body color); "composite" layer carries logo/handle/lock
    // pre-combined, transparent background, stacked on top via CSS. Layers with no layerRole
    // (legacy flat pre-baked photos) are treated as "body" so old data keeps working. See
    // safe_image_layer metaobjects / *-safe-images/ for how these are authored.
    const bodyImageLayers = configuredImages.filter((entry) => entry.layerRole !== 'composite' && entry.layerRole !== 'hinge');
    const hingeImageLayers = configuredImages.filter((entry) => entry.layerRole === 'hinge');
    const hardwareImageLayers = configuredImages.filter((entry) => entry.layerRole === 'composite');
    const groupsById = new Map(data.optionGroups.map((g) => [g.id, g]));
    const availableGroupIds = new Set(data.model.availableOptionGroupIds || []);

    const sizeGroup = data.optionGroups.find((g) => g.role === 'size' && availableGroupIds.has(g.id));
    const otherGroups = data.optionGroups
      .filter((g) => g.role !== 'size' && availableGroupIds.has(g.id))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const valuesByGroup = new Map();
    for (const v of data.optionValues) {
      if (!v.groupId) continue;
      if (!valuesByGroup.has(v.groupId)) valuesByGroup.set(v.groupId, []);
      valuesByGroup.get(v.groupId).push(v);
    }
    // No-cost options first, then cheapest to priciest; sortOrder only breaks ties within
    // the same price (e.g. multiple $0 or multiple +$150 finishes keep their curated order).
    for (const list of valuesByGroup.values()) {
      list.sort((a, b) => (a.priceModifierValue || 0) - (b.priceModifierValue || 0) || a.sortOrder - b.sortOrder);
    }

    function findValueByCode(groupId, code) {
      return (valuesByGroup.get(groupId) || []).find((v) => v.code === code) || null;
    }

    // Weight label for a size config. Thunderbolt's AR500 liner adds weight, so that option_value
    // carries dependencies.size_weight = { [sizeCode]: lbs } and the safe shows both numbers
    // on its own line under the standard weight. Any size with no such entry shows just the plain weight.
    function weightParts(config) {
      if (!config || !config.weightLbs) return null;
      const fmt = (n) => Number(n).toLocaleString('en-US');
      for (const groupId of availableGroupIds) {
        for (const v of valuesByGroup.get(groupId) || []) {
          const withLiner = v.dependencies && v.dependencies.size_weight && v.dependencies.size_weight[config.sizeCode];
          if (withLiner != null) return { base: fmt(config.weightLbs) + ' lbs', liner: fmt(withLiner) + ' lbs with AR500 Liner' };
        }
      }
      return { base: fmt(config.weightLbs) + ' lbs', liner: null };
    }

    // An option_value's real price contribution given the CURRENT size/hardware/trim context.
    // Some values only carry a premium (or discount) in combination with specific hardware, and
    // that amount itself scales with size (e.g. Classic Plus's marble finishes + Black Chrome,
    // Centurion's door panel and 3-Spoke handle) — a flat priceModifierValue can't express that,
    // so a value can instead carry dependencies.price_by_size = { [hardwareCode]: { [sizeCode]:
    // amount } }. Others only carry a premium under one trim level, scaling by size (e.g.
    // Lincoln's Gloss finishes, which cost more than the Standard-trim Marble baseline price but
    // cost the same as every other finish under Pro Flex/Signature's flat "all finishes" pricing)
    // — those use dependencies.price_by_trim_and_size = { [trimCode]: { [sizeCode]: amount } }
    // instead, mirroring the existing capacity_by_trim precedent (see updateSizeCapacities). A
    // trim level itself can also just scale by size with no other condition (Lincoln's Pro
    // Flex/Signature premiums are flat "all finishes" but still bigger on a bigger safe) —
    // dependencies.size_price = { [sizeCode]: amount } covers that, size only, no hardware or
    // trim gate. Checked in that order (hardware, then trim, then plain size); a value with a
    // matching entry in an earlier tier wins if it somehow carries more than one. No-op (returns
    // the flat priceModifierValue) for any value without a matching dependency, or when there's
    // no size/hardware/trim selected yet to look it up by. Shared by the running total in
    // render() and the per-option price badge in renderOptionsFor(), so the number a shopper
    // sees on a card always matches what it actually adds to the total.
    function effectiveModifier(value) {
      const sizeCode = state.sizeConfig ? state.sizeConfig.sizeCode : null;

      const bySize = value.dependencies && value.dependencies.price_by_size;
      const hardwareCode = state.selections.rhino_trim;
      const byHardware = bySize && hardwareCode ? bySize[hardwareCode] : null;
      const hwOverride = byHardware && sizeCode && Object.prototype.hasOwnProperty.call(byHardware, sizeCode)
        ? byHardware[sizeCode]
        : null;
      if (hwOverride != null) return Number(hwOverride) || 0;

      const byTrim = value.dependencies && value.dependencies.price_by_trim_and_size;
      const trimCode = state.selections.trim_level;
      const byTrimSize = byTrim && trimCode ? byTrim[trimCode] : null;
      const trimOverride = byTrimSize && sizeCode && Object.prototype.hasOwnProperty.call(byTrimSize, sizeCode)
        ? byTrimSize[sizeCode]
        : null;
      if (trimOverride != null) return Number(trimOverride) || 0;

      const flatBySize = value.dependencies && value.dependencies.size_price;
      const sizeOnlyOverride = flatBySize && sizeCode && Object.prototype.hasOwnProperty.call(flatBySize, sizeCode)
        ? flatBySize[sizeCode]
        : null;
      if (sizeOnlyOverride != null) return Number(sizeOnlyOverride) || 0;

      return Number(value.priceModifierValue) || 0;
    }

    // A group's default value: a per-model override (data.model.defaultSelections, keyed by
    // group handle) takes priority over the group-wide option_value.isDefault flag. Lets shared
    // option_values (reused across every Rhino/AMSEC line) carry one "usual" default while
    // still letting an individual model's real standard configuration differ.
    //
    // allowedCodes (optional) scopes the isDefault fallback search to only codes valid for the
    // current model/size — required because some shared groups (e.g. lock_type: Franklin's
    // EDRIVE, Colonial's COLONIAL_ELOCK, Lincoln's/Presidential's MECHANICAL) hold more than one
    // isDefault:true value, one per model, all in the same group. Without scoping, .find() would
    // just return whichever is_default value happens to come first for every model.
    const modelDefaultOverrides = (data.model && data.model.defaultSelections) || {};
    function defaultValueForGroup(group, allowedCodes) {
      const overrideCode = modelDefaultOverrides[group.handle];
      if (overrideCode) {
        const overridden = findValueByCode(group.id, overrideCode);
        if (overridden && (!allowedCodes || allowedCodes.indexOf(overridden.code) !== -1)) return overridden;
      }
      const candidates = valuesByGroup.get(group.id) || [];
      const scoped = allowedCodes ? candidates.filter((v) => allowedCodes.indexOf(v.code) !== -1) : candidates;
      // scoped is already curated no-cost-first/cheapest-first order (see valuesByGroup's own
      // sort above), so falling back to its first entry when nothing is explicitly flagged
      // is exactly "the first option in this group" — every group lands on a real selection
      // instead of leaving the shopper to click through every step manually.
      return scoped.find((v) => v.isDefault) || scoped[0] || null;
    }

    const state = {
      sizeConfig: null,
      selections: {}, // groupHandle -> code
      clearedNotice: null, // groupHandle of a selection just cleared by a dependency change
    };

    function allowedCodesForGroup(group) {
      if (!state.sizeConfig) return null; // null = "choose a size first"
      const fromSize = state.sizeConfig.availableOptionCodes || [];
      return fromSize;
    }

    /** Dependency-based filtering (e.g. accent color -> allowed body colors), by group role.
     * Intersects every EARLIER-ordered selected group's allowed_values for this target, rather
     * than stopping at the first one found or considering every group regardless of order —
     * Centurion has both handle_style and accent_color separately restricting body_color (Drop
     * handle -> only Black; 3-Spoke -> Black/Bronze/White; Brass hardware -> only Bronze), and
     * returning early on whichever group happens to sort first would silently drop the other's
     * narrower restriction. Only considering EARLIER groups (lower sortOrder) matters too: a
     * body_color value also carries a reverse allowed_values.accent_color (so clicking a color
     * directly can auto-correct an incompatible hardware pick, see the click handler below) —
     * but body_color sorts AFTER accent_color, so that reverse mapping must never narrow which
     * accent_color options are shown (it already hid Brass entirely whenever Black or White
     * happened to be selected, since neither's reverse list included it). No-op (same result as
     * before) for any model where only one EARLIER group ever restricts a given target. */
    function dependencyAllowList(group) {
      let result = null;
      for (const otherGroup of otherGroups) {
        if (otherGroup.sortOrder >= group.sortOrder) continue;
        const selectedCode = state.selections[otherGroup.handle];
        if (!selectedCode) continue;
        const selectedValue = findValueByCode(otherGroup.id, selectedCode);
        if (!selectedValue || !selectedValue.dependencies) continue;
        const bySize = selectedValue.dependencies.allowed_values_by_size;
        const sizeScoped = bySize && state.sizeConfig && bySize[state.sizeConfig.sizeCode];
        const allowed = sizeScoped || selectedValue.dependencies.allowed_values;
        if (allowed && Object.prototype.hasOwnProperty.call(allowed, group.handle)) {
          const list = allowed[group.handle];
          result = result === null ? list : result.filter((code) => list.indexOf(code) !== -1);
        }

        // combo_hardware pins body_color -> an exact accent_color for a handle-conditioned
        // pairing (see the click handler) — when resolving body_color's own visible list, also
        // narrow by its INVERSE: if the currently selected accent_color only pairs with certain
        // colors under this handle, those are the only colors that should show, even though
        // accent_color's own allowed_values.body_color is necessarily permissive (Black Chrome
        // is genuinely valid with both Black, under Drop, and White, under 3-Spoke — so its
        // forward list alone can't rule Black out once 3-Spoke + Black Chrome are both active).
        // No-op for any group other than body_color, or any handle without combo_hardware
        // (every model, and Centurion's Drop handle, today).
        if (group.handle === 'body_color' && otherGroup.handle === 'handle_style' && selectedValue.dependencies.combo_hardware) {
          const accentCode = state.selections.accent_color;
          if (accentCode) {
            const comboHardware = selectedValue.dependencies.combo_hardware;
            const validColors = Object.keys(comboHardware).filter((colorCode) => comboHardware[colorCode] === accentCode);
            if (validColors.length) {
              result = result === null ? validColors : result.filter((code) => validColors.indexOf(code) !== -1);
            }
          }
        }
      }
      return result;
    }

    // Conditional steps (e.g. Lincoln's Paint Style, which exists only on Premium Signature
    // trim — not rendered at all otherwise, no disabled placeholder). A group with no
    // visibleWhen is always visible (every existing group, on every model, today).
    //
    // A group this size offers zero values for at all (e.g. Centurion 12/18 have no
    // handle_style values in their availableOptionCodes, since handle choice only exists on
    // 24/32) is hidden outright too — otherwise it would still render as an empty step with
    // no options in it. No-op for any model whose every group has at least one value per size
    // (every model before Centurion).
    function isGroupVisible(group) {
      const sizeAllowed = allowedCodesForGroup(group);
      if (sizeAllowed) {
        const hasAny = (valuesByGroup.get(group.id) || []).some((v) => sizeAllowed.indexOf(v.code) !== -1);
        if (!hasAny) return false;
      }
      if (!group.visibleWhen) return true;
      const selected = state.selections[group.visibleWhen.group];
      return Boolean(selected && group.visibleWhen.codes.indexOf(selected) !== -1);
    }

    // What a group's selection "counts as" for dependencies.requires matching elsewhere (e.g.
    // body_color values keyed to a paint_style code). A visible group's real selection is used
    // as-is. A group that isn't even shown right now (e.g. Paint Style on Standard/Pro Flex,
    // where there is no 2-Tone concept at all) counts as its default value instead of nothing —
    // otherwise every requires-gated value in a dependent group would wrongly exclude itself
    // forever, since an unselected hidden step can never literally match a required code.
    function effectiveSelection(handle) {
      const groupDef = otherGroups.find((g) => g.handle === handle);
      if (groupDef && !isGroupVisible(groupDef)) {
        // A group like "paint_style" can be shared across models (Lincoln and Presidential
        // both use it) — its option_values include both brands' codes under one group id.
        // Scope the default lookup to codes this model's current size actually offers,
        // otherwise this could pick another model's default (e.g. LINCOLN_1TONE) instead of
        // this one's (PRESIDENTIAL_1TONE), and every requires-gated value would still fail
        // to match.
        const sizeAllowed = (state.sizeConfig && state.sizeConfig.availableOptionCodes) || [];
        const defaultValue = defaultValueForGroup(groupDef, sizeAllowed);
        if (defaultValue) return defaultValue.code;
      }
      return state.selections[handle];
    }

    function buildSizeStep() {
      const step = document.createElement('section');
      step.className = 'rc__step';
      const groupLabel = sizeGroup ? sizeGroup.titleName || sizeGroup.displayName : 'Choose your size';
      step.innerHTML =
        '<div class="rc__step-head"><h2 class="rc__step-title">' + esc(groupLabel) + '</h2>' +
        '<span class="rc__step-value" data-rc-size-label>Select a size</span></div>' +
        '<div class="rc__grid" style="--rc-cols:3" data-rc-size-options role="group" aria-label="' + esc(groupLabel) + '"></div>';
      stepsEl.appendChild(step);

      // model_size_configuration metaobjects have no ordering guarantee of their own from
      // shop.metaobjects — sort the cards by the underlying size option_value's sortOrder
      // (e.g. so "25" always renders before "40") rather than showing them in whatever
      // order Liquid happened to iterate the metaobject list.
      const optionsEl = step.querySelector('[data-rc-size-options]');
      optionsEl.innerHTML = sizeConfigs
        .slice()
        .sort((a, b) => {
          const va = sizeGroup ? findValueByCode(sizeGroup.id, a.sizeCode) : null;
          const vb = sizeGroup ? findValueByCode(sizeGroup.id, b.sizeCode) : null;
          return (va ? va.sortOrder : 999) - (vb ? vb.sortOrder : 999);
        })
        .map((config) => {
          const value = sizeGroup ? findValueByCode(sizeGroup.id, config.sizeCode) : null;
          // title=config.name gives a native hover tooltip with the size name — the visible
          // label (rc__opt-name) already shows it too, but a shopper hovering just the diagram
          // image (no text under the cursor) should still see what size they're looking at.
          const img = value && value.sizeImage
            ? '<img src="' + esc(value.sizeImage) + '" alt="' + esc(config.name) + '" title="' + esc(config.name) + '">'
            : '';
          // Exterior/interior dimensions and weight come from model_size_configuration (not
          // the size option_value) since they're real per-model-per-size specs sourced from
          // each product's own description table, not shared/curated option data.
          const specLines = [];
          if (config.exteriorDimensions) specLines.push('<span class="rc__opt-spec">Ext: ' + esc(config.exteriorDimensions) + '</span>');
          if (config.interiorDimensions) specLines.push('<span class="rc__opt-spec">Int: ' + esc(config.interiorDimensions) + '</span>');
          const weight = weightParts(config);
          if (weight) {
            specLines.push('<span class="rc__opt-spec">' + esc(weight.base) + '</span>');
            if (weight.liner) specLines.push('<span class="rc__opt-spec">' + esc(weight.liner) + '</span>');
          }
          const specs = specLines.length ? '<span class="rc__opt-specs">' + specLines.join('') + '</span>' : '';
          return (
            '<button type="button" class="rc__opt rc__opt--size" data-rc-size="' + esc(config.id) + '" aria-pressed="false" title="' + esc(config.name) + '">' +
            img +
            '<span class="rc__opt-name">' + esc(config.name) + '</span>' +
            specs +
            '</button>'
          );
        })
        .join('');
    }

    function buildOptionStep(group) {
      const step = document.createElement('section');
      step.className = 'rc__step';
      step.dataset.rcGroupHandle = group.handle;
      const title = group.titleName || group.displayName;
      step.innerHTML =
        '<div class="rc__step-head"><h2 class="rc__step-title">' + esc(title) + '</h2>' +
        '<span class="rc__step-value" data-rc-label>Select an option</span></div>' +
        (group.description ? '<p class="rc__step-description">' + esc(group.description) + '</p>' : '') +
        (group.learnMoreUrl
          ? '<a class="rc__step-learn-more" href="' + esc(group.learnMoreUrl) + '" target="_blank" rel="noopener">' + esc(group.learnMoreLabel || 'Learn more') + '</a>'
          : '') +
        (group.uiType === 'swatch'
          ? '<div class="rc__swatch-row" data-rc-options role="group" aria-label="' + esc(title) + '"></div>'
          : '<div class="rc__grid" style="--rc-cols:' + (group.columnAmount || 3) + '" data-rc-options role="group" aria-label="' + esc(title) + '"></div>') +
        '<p class="rc__step-note" data-rc-note aria-live="polite"></p>';
      stepsEl.appendChild(step);
    }

    function renderOptionsFor(group) {
      const step = stepsEl.querySelector('[data-rc-group-handle="' + group.handle + '"]');
      if (!step) return;
      const optionsEl = step.querySelector('[data-rc-options]');
      const noteEl = step.querySelector('[data-rc-note]');
      const labelEl = step.querySelector('[data-rc-label]');
      const allValues = valuesByGroup.get(group.id) || [];
      const sizeAllowed = allowedCodesForGroup(group);
      const depAllowed = dependencyAllowList(group);
      const selectedCode = state.selections[group.handle];

      if (!sizeAllowed) {
        optionsEl.innerHTML = '';
        noteEl.textContent = 'Choose a size first.';
        labelEl.textContent = 'Select a size first';
        return;
      }

      // Option groups are shared across every Rhino series (Franklin, Colonial, ...), so
      // valuesByGroup can hold another series' values too. Only render the ones that actually
      // belong to this model/size — anything else isn't "temporarily unavailable" (which is
      // what disabled-but-visible communicates), it's just irrelevant to this product.
      let values = allValues.filter((v) => sizeAllowed.indexOf(v.code) !== -1);

      // dependencies.requires excludes a value from the picker entirely unless the referenced
      // selections match — e.g. Lincoln's 4 2-Tone colorways only exist when paint_style is
      // 2-Tone, and its 15 1-Tone colors only exist when paint_style is 1-Tone. Two disjoint
      // value pools in the same group, cleanly swapped rather than shown disabled. No-op for
      // any value without a requires clause (everything on Franklin/Colonial today).
      values = values.filter((v) => {
        const requires = v.dependencies && v.dependencies.requires;
        if (!requires) return true;
        return Object.keys(requires).every((h) => effectiveSelection(h) === requires[h]);
      });

      // A value excluded by the current selection's dependencies.allowed_values (e.g. Black
      // Chrome accent -> only certain body colors) is dropped from the picker entirely rather
      // than shown grayed out — a disabled swatch a customer can still see and click on reads as
      // a bug, not as "unavailable right now".
      values = values.filter((v) => !(depAllowed && depAllowed.indexOf(v.code) === -1));

      const isBody = group.role === 'body_color';
      const isLock = group.role === 'lock_type';

      optionsEl.innerHTML = values
        .map((v) => {
          const pressed = selectedCode === v.code;
          const modifier = effectiveModifier(v);
          // Only badge upcharges. A discount (e.g. Lincoln's marble finishes, Magnum's) is a real
          // negative effectiveModifier, but showing "- $600" next to a color reads as a strange
          // thing to advertise mid-picker — the shopper just sees the running total drop instead.
          const priceLabel = modifier > 0 ? '<span class="rc__opt-price">' + delta(modifier) + '</span>' : '';
          const priceSwatchLabel = modifier > 0 ? '<span class="rc__swatch-price">' + delta(modifier) + '</span>' : '';

          if (group.uiType === 'swatch') {
            const chipClass = 'rc__swatch-chip' + (isBody ? ' rc__swatch-chip--body' : '');
            // Two-Tone colors (e.g. Lincoln's 4 colorways) render as a split gradient — the
            // body color over a fixed black accent band — matching the source design's
            // twoToneAccent treatment, not a flat chip like a normal 1-Tone/accent swatch.
            const chip = v.finishType === 'two_tone' && v.hexColor
              ? '<span class="' + chipClass + '" style="background:linear-gradient(180deg,' + esc(v.hexColor) + ' 0 68%,#16181A 68% 100%)" aria-hidden="true"></span>'
              : v.optionImage
              ? '<span class="' + chipClass + '" style="background-image:url(' + esc(v.optionImage) + ')" aria-hidden="true"></span>'
              : v.hexColor
              ? '<span class="' + chipClass + '" style="background:' + esc(v.hexColor) + '" aria-hidden="true"></span>'
              : '<span class="' + chipClass + ' rc__swatch-chip--' + esc((v.code || '').toLowerCase()) + '" aria-hidden="true"></span>';
            const swatchMeta = v.tooltip ? '<span class="rc__swatch-meta">' + esc(v.tooltip) + '</span>' : '';
            return (
              '<button type="button" class="rc__swatch' + (isBody ? ' rc__swatch--body' : '') + '" data-rc-value="' + esc(v.code) + '" aria-pressed="' + pressed + '" title="' + esc(v.label) + '">' +
              chip + '<span class="rc__swatch-name">' + esc(v.label) + '</span>' + swatchMeta + priceSwatchLabel + '</button>'
            );
          }

          const img = v.optionImage ? '<img src="' + esc(v.optionImage) + '" alt="">' : '';
          if (isLock) {
            return (
              '<button type="button" class="rc__opt rc__opt--lock" data-rc-value="' + esc(v.code) + '" aria-pressed="' + pressed + '">' +
              img + '<span class="rc__opt-text"><span class="rc__opt-name">' + esc(v.label) + '</span>' +
              (v.tooltip ? '<span class="rc__opt-meta">' + esc(v.tooltip) + '</span>' : '') +
              priceLabel + '</span></button>'
            );
          }
          return (
            '<button type="button" class="rc__opt" data-rc-value="' + esc(v.code) + '" aria-pressed="' + pressed + '">' +
            img + '<span class="rc__opt-name">' + esc(v.label) + '</span>' + priceLabel +
            '</button>'
          );
        })
        .join('');

      const selectedValue = selectedCode ? findValueByCode(group.id, selectedCode) : null;
      labelEl.textContent = selectedValue ? selectedValue.label : 'Select an option';
      if (state.clearedNotice === group.handle) {
        noteEl.textContent = 'Your previous selection isn’t available with your current choices — pick a new option.';
      } else {
        noteEl.textContent = '';
      }
    }

    // Trim-dependent capacity text on size cards (e.g. the Colonial 50 shows "60 to 64 long
    // guns..." until a trim is picked, then the trim-specific figure). No-op for any size
    // option_value without a dependencies.capacity_by_trim entry (e.g. all of Franklin's).
    function updateSizeCapacities() {
      if (!sizeGroup) return;
      const trimCode = state.selections.trim_level;
      sizeConfigs.forEach((config) => {
        const metaEl = stepsEl.querySelector('[data-rc-size-meta="' + config.id + '"]');
        if (!metaEl) return;
        const value = findValueByCode(sizeGroup.id, config.sizeCode);
        const byTrim = value && value.dependencies && value.dependencies.capacity_by_trim;
        const text = byTrim && trimCode && byTrim[trimCode] ? byTrim[trimCode] : (value ? value.tooltip : '');
        if (text) metaEl.textContent = text;
      });
    }

    // Key Features strip (Capacity / Weight) — the only two tiles that vary by size, since
    // this configurator has no Shopify variant to hang per-size metafields on. Mirrors
    // safe-builder.liquid's "Select a size to view" fallback so the tiles read the same way
    // before a size is picked, and gracefully show nothing extra when a size has no gun
    // capacity data at all (e.g. sizes with no gun-capacity spec published).
    function updateKeyFeatures() {
      if (specCapacityEl) {
        if (!state.sizeConfig) {
          specCapacityEl.textContent = 'Select a size to view';
        } else if (state.sizeConfig.gunCapacity) {
          specCapacityEl.textContent = 'Up to ' + state.sizeConfig.gunCapacity + ' guns';
        } else if (state.sizeConfig.cubicFt) {
          // Non-gun-safe capacity (vault doors, cabinets, etc.) — cubic feet instead of
          // "None". Client feedback 2026-09-23.
          specCapacityEl.textContent = state.sizeConfig.cubicFt + ' cu. ft.';
        } else {
          specCapacityEl.textContent = 'None';
        }
      }
      if (specWeightEl) {
        const weight = state.sizeConfig ? weightParts(state.sizeConfig) : null;
        if (!state.sizeConfig) {
          specWeightEl.textContent = 'Select a size to view';
        } else if (!weight) {
          specWeightEl.textContent = '—';
        } else {
          specWeightEl.innerHTML = esc(weight.base) +
            (weight.liner ? '<span class="rc__wt-liner">' + esc(weight.liner) + '</span>' : '');
        }
      }
    }

    // Real configured-safe photography (safe_image_layer metaobjects, AI-recolored/retextured
    // per size + body color + hardware color + lock combination — see *-safe-images/ workflow).
    // A layer "matches" the current selection if, for each of its four axis lists that are
    // non-empty, the corresponding current selection appears in that list. An empty list on an
    // entry means "doesn't care about this axis" (e.g. a body photo that's the same across every
    // lock). Among matching layers, the one constraining the most axes wins — this lets a
    // fully-specified legacy flat photo still take priority over a more generic layered one for
    // the same combo. No-op (returns null, falls back to the product's normal media) for any
    // combination that hasn't been photographed yet.
    // hinge_swing is a plain single value (not a list like the other axes, since a photo is
    // shot for exactly one hinge side, never a curated set) — wildcards the same way an empty
    // list does: an entry with no hingeSwing matches any/no hinge selection, and a criteria
    // with no hingeSwing (every non-hinge-aware model) never filters on it either. This keeps
    // every existing model's matches byte-for-byte unchanged; only a layer that explicitly sets
    // hinge_swing (see safe_image_layer) and a selection that resolves one (see
    // currentHingeSwing()) start narrowing on it.
    function hingeOk(entryHinge, currentHinge) {
      return !entryHinge || !currentHinge || entryHinge === currentHinge;
    }

    // Same wildcard-when-unset shape as hingeOk, for the separate in/out swing axis (Beast
    // Vault Door's fully-flattened photos are shot per exact hinge side AND swing direction —
    // hinge_swing alone can't tell an in-swing photo from an out-swing one for the same hinge
    // side). Every existing model's layers leave swing_direction blank, so this always wildcards
    // for them, same as hingeOk did when hinge_swing first shipped.
    function swingOk(entrySwing, currentSwing) {
      return !entrySwing || !currentSwing || entrySwing === currentSwing;
    }

    function bestImageMatch(layers, criteria) {
      const axisOk = (list, current) => !list || list.length === 0 || (Boolean(current) && list.indexOf(current) !== -1);
      let best = null;
      let bestSpecificity = -1;
      for (const entry of layers) {
        if (
          !axisOk(entry.sizeCodes, criteria.size) ||
          !axisOk(entry.colorCodes, criteria.color) ||
          !axisOk(entry.hardwareColorCodes, criteria.hardwareColor) ||
          !axisOk(entry.lockTypeCodes, criteria.lockType) ||
          !axisOk(entry.requiredOptionCodes, criteria.requiredOption) ||
          !hingeOk(entry.hingeSwing, criteria.hingeSwing) ||
          !swingOk(entry.swingDirection, criteria.swingDirection)
        ) {
          continue;
        }
        const specificity =
          (entry.sizeCodes && entry.sizeCodes.length ? 1 : 0) +
          (entry.colorCodes && entry.colorCodes.length ? 1 : 0) +
          (entry.hardwareColorCodes && entry.hardwareColorCodes.length ? 1 : 0) +
          (entry.lockTypeCodes && entry.lockTypeCodes.length ? 1 : 0) +
          (entry.requiredOptionCodes && entry.requiredOptionCodes.length ? 1 : 0) +
          (entry.hingeSwing ? 1 : 0) +
          (entry.swingDirection ? 1 : 0);
        if (specificity > bestSpecificity) {
          best = entry;
          bestSpecificity = specificity;
        }
      }
      return best;
    }

    // Hinge-overlay-specific match: color and hinge side are HARD requirements (an overlay
    // photographed for the wrong body color or wrong hinge side must never show), but cap/
    // hardware-color is only a soft preference. Most Beast Vault Door colors only ever got a
    // single cap-color shot (black) — under bestImageMatch()'s all-axes-are-hard-filters rule,
    // picking Chrome or Gold accent color on one of those colors would resolve to no hinge
    // overlay at all. Scoring the hardware match instead (bonus when it matches, but not
    // disqualifying when it doesn't) means the shopper always sees the correct body-color
    // hinge photo, just not always in their exact chosen cap finish — strictly better than
    // showing nothing. Body/composite (lock) layers keep using plain bestImageMatch() above;
    // this only applies to the hinge overlay.
    function bestHingeMatch(layers, criteria) {
      const axisOk = (list, current) => !list || list.length === 0 || (Boolean(current) && list.indexOf(current) !== -1);
      let best = null;
      let bestScore = -1;
      for (const entry of layers) {
        if (
          !axisOk(entry.sizeCodes, criteria.size) ||
          !axisOk(entry.colorCodes, criteria.color) ||
          !hingeOk(entry.hingeSwing, criteria.hingeSwing)
        ) {
          continue;
        }
        const hardwareMatches = axisOk(entry.hardwareColorCodes, criteria.hardwareColor);
        const score =
          (entry.sizeCodes && entry.sizeCodes.length ? 1 : 0) +
          (entry.colorCodes && entry.colorCodes.length ? 1 : 0) +
          (hardwareMatches ? 2 : 0) +
          (entry.hingeSwing ? 1 : 0);
        if (score > bestScore) {
          best = entry;
          bestScore = score;
        }
      }
      return best;
    }

    // Open-door hover photo is shot once per (size, body color, trim) and reused across every
    // hardware finish and lock — see the safe_image_layer.open_image_url comment below — so it
    // must be resolved independently of bodyMatch (which also filters on hardwareColor/lockType
    // to pick the CLOSED photo). Without this, a combo whose specific hw+lock row has no open
    // photo would show no hover at all, even when a sibling row for the same color+trim (a
    // different lock/hw) has the exact same open photo attached. hardwareColor and lockType are
    // deliberately absent from both the filter and the specificity count here.
    function bestOpenMatch(layers, criteria) {
      const axisOk = (list, current) => !list || list.length === 0 || (Boolean(current) && list.indexOf(current) !== -1);
      let best = null;
      let bestSpecificity = -1;
      for (const entry of layers) {
        if (!entry.openImageUrl) continue;
        if (
          !axisOk(entry.sizeCodes, criteria.size) ||
          !axisOk(entry.colorCodes, criteria.color) ||
          !axisOk(entry.requiredOptionCodes, criteria.requiredOption) ||
          !hingeOk(entry.hingeSwing, criteria.hingeSwing)
        ) {
          continue;
        }
        const specificity =
          (entry.sizeCodes && entry.sizeCodes.length ? 1 : 0) +
          (entry.colorCodes && entry.colorCodes.length ? 1 : 0) +
          (entry.requiredOptionCodes && entry.requiredOptionCodes.length ? 1 : 0) +
          (entry.hingeSwing ? 1 : 0);
        if (specificity > bestSpecificity) {
          best = entry;
          bestSpecificity = specificity;
        }
      }
      return best;
    }

    // Reads whichever currently-selected option_value (across every visible group, not just one
    // hardcoded handle — this file stays generic across every Rhino series, see header
    // comment) carries a dependencies.hinge_swing string, e.g. a combined "In-Swing | Left
    // Hinge" style/door-style value. Returns null for every model that doesn't wire this up
    // (the vast majority), which is exactly the wildcard hingeOk() above needs to no-op for them.
    function currentHingeSwing() {
      for (const group of otherGroups) {
        const code = state.selections[group.handle];
        if (!code) continue;
        const value = findValueByCode(group.id, code);
        if (value && value.dependencies && value.dependencies.hinge_swing) {
          return value.dependencies.hinge_swing;
        }
      }
      return null;
    }

    // Same lookup as currentHingeSwing() but for dependencies.swing ('in'/'out'), e.g. Beast
    // Vault Door's combined "In-Swing | Left Hinge" style value. Generic across any model —
    // returns null for every model that doesn't set this dependency.
    function currentSwingDirection() {
      for (const group of otherGroups) {
        const code = state.selections[group.handle];
        if (!code) continue;
        const value = findValueByCode(group.id, code);
        if (value && value.dependencies && value.dependencies.swing) {
          return value.dependencies.swing;
        }
      }
      return null;
    }

    // Static thumbnail rail: showcase this model's own photography (TSG-hosted, the same
    // safe_image_layer data bestImageMatch draws from) instead of whatever happens to be
    // uploaded to the Shopify product's media gallery. One thumb per distinct body color,
    // capped well under the full size/hardware/lock catalog — a quick "browse the finishes"
    // strip, not a duplicate of the configurator. Every thumb is resolved against the model's
    // DEFAULT size/hardware/lock (same axes updateConfiguredHero uses), so a model like
    // Colonial — whose body photo has no hardware/logo baked in, unlike most Rhino models'
    // flat pre-baked photos — gets the matching composite (logo/handle/lock) and hinge overlay
    // layered on top of each thumb too, not just a bare body shot. Called from buildAll() once
    // applyDefaultSelections() has populated state.sizeConfig/selections; the first ("Your
    // build") thumb is untouched — it keeps mirroring the live configuration as before.
    function buildAutoGalleryThumbs() {
      const thumbsEl = root.querySelector('.rc__thumbs');
      if (!thumbsEl || !state.sizeConfig) return;

      const MAX_AUTO_THUMBS = 10;
      const sizeCode = state.sizeConfig.sizeCode;
      const hardwareCode = state.selections.rhino_trim;
      const lockCode = state.selections.rhino_lock_type;
      const hingeCode = currentHingeSwing();
      const swingCode = currentSwingDirection();
      const trimCode = state.selections.trim_level || null;

      // Distinct body colors this model has photography for, in authored (sort_order) order —
      // one showcase thumb per color, not every color/hardware/lock combination.
      const colorCodes = [];
      const seenColors = new Set();
      bodyImageLayers
        .slice()
        .sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999))
        .forEach((layer) => {
          (layer.colorCodes || []).forEach((code) => {
            if (!seenColors.has(code)) {
              seenColors.add(code);
              colorCodes.push(code);
            }
          });
        });

      const picks = [];
      for (const colorCode of colorCodes) {
        const bodyMatch = bestImageMatch(bodyImageLayers, {
          size: sizeCode,
          color: colorCode,
          hardwareColor: hardwareCode,
          lockType: lockCode,
          hingeSwing: hingeCode,
          swingDirection: swingCode,
          requiredOption: trimCode,
        });
        if (!bodyMatch || !bodyMatch.imageUrl) continue;

        const hingeMatch = swingCode !== 'in'
          ? bestHingeMatch(hingeImageLayers, { size: sizeCode, color: colorCode, hardwareColor: hardwareCode, hingeSwing: hingeCode })
          : null;
        const hwMatch = bestImageMatch(hardwareImageLayers, {
          size: sizeCode,
          hardwareColor: hardwareCode,
          lockType: lockCode,
          hingeSwing: hingeCode,
        });

        picks.push({
          bodyUrl: bodyMatch.imageUrl,
          hingeUrl: hingeMatch ? hingeMatch.imageUrl : null,
          hwUrl: hwMatch ? hwMatch.imageUrl : null,
        });
        if (picks.length >= MAX_AUTO_THUMBS) break;
      }

      if (!picks.length) return; // no photography to show yet — leave just the "Your build" thumb

      const frag = document.createDocumentFragment();
      picks.forEach((pick) => {
        const layered = Boolean(pick.hingeUrl || pick.hwUrl);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = layered ? 'rc__thumb rc__thumb--layered' : 'rc__thumb';
        btn.setAttribute('data-rc-photo', pick.bodyUrl);
        btn.setAttribute('data-rc-photo-alt', (data.model && data.model.name) || '');
        if (pick.hingeUrl) btn.setAttribute('data-rc-photo-hinge', pick.hingeUrl);
        if (pick.hwUrl) btn.setAttribute('data-rc-photo-hardware', pick.hwUrl);
        btn.setAttribute('aria-pressed', 'false');

        const bodyImg = document.createElement('img');
        if (layered) bodyImg.className = 'rc__thumb-layer';
        bodyImg.src = pick.bodyUrl;
        bodyImg.alt = '';
        bodyImg.loading = 'lazy';
        btn.appendChild(bodyImg);

        // Appended in body -> hinge -> hardware order, same stacking as the hero — with no
        // explicit z-index on plain .rc__thumb-layer, later-in-DOM paints on top, so this order
        // alone gives the right result.
        if (pick.hingeUrl) {
          const hingeImg = document.createElement('img');
          hingeImg.className = 'rc__thumb-layer';
          hingeImg.src = pick.hingeUrl;
          hingeImg.alt = '';
          hingeImg.loading = 'lazy';
          btn.appendChild(hingeImg);
        }
        if (pick.hwUrl) {
          const hwImg = document.createElement('img');
          hwImg.className = 'rc__thumb-layer';
          hwImg.src = pick.hwUrl;
          hwImg.alt = '';
          hwImg.loading = 'lazy';
          btn.appendChild(hwImg);
        }

        frag.appendChild(btn);
      });
      thumbsEl.appendChild(frag);
    }

    function updateConfiguredHero() {
      if (!heroEl) return;
      // Remember the product's own default hero image once, so we can restore it when the
      // current selection no longer matches any photographed combination.
      if (heroEl.dataset.rcDefaultSrc === undefined) heroEl.dataset.rcDefaultSrc = heroEl.getAttribute('src') || '';

      const sizeCode = state.sizeConfig ? state.sizeConfig.sizeCode : null;
      const colorCode = state.selections.rhino_color;
      const hardwareCode = state.selections.rhino_trim;
      const lockCode = state.selections.rhino_lock_type;
      const hingeCode = currentHingeSwing();
      const swingCode = currentSwingDirection();
      // Premium Signature builds (Lincoln) have a different exterior (5-spoke handle) than
      // Standard/ProFlex, which share one photo — see the requiredOptionCodes comment in
      // product-configurator-rhino.liquid. null on any model without a trim_level group, which
      // bestImageMatch's axisOk() wildcards the same as every other unset axis.
      const trimCode = state.selections.trim_level || null;

      const bodyMatch = sizeCode
        ? bestImageMatch(bodyImageLayers, { size: sizeCode, color: colorCode, hardwareColor: hardwareCode, lockType: lockCode, hingeSwing: hingeCode, swingDirection: swingCode, requiredOption: trimCode })
        : null;
      const targetBodyUrl = bodyMatch ? bodyMatch.imageUrl : heroEl.dataset.rcDefaultSrc;

      // Only ever composite the hinge/hardware overlays on top of a real, photographed body
      // image (bodyMatch) — never on the product's default/placeholder photo. Without this
      // gate, picking a hardware finish + lock before a body color (or on any combo the body
      // layer has no photography for) would still resolve a match on its own axes and draw
      // hinges/dial/handle floating over an unrelated stock photo.
      // Hinge overlay is color-specific (it's a crop of that exact body color's own hinge
      // edge), unlike the hardware/lock overlay which is color-agnostic — so it's matched by
      // color + hinge side + cap/hardware finish, never by lockType. It's also swing-direction
      // gated: an In-Swing door's hinge sits on the inside and was never photographed, so the
      // overlay only ever shows for Out-Swing (swingCode === 'in' forces it off; a model with
      // no swing concept at all leaves swingCode null and this gate is a no-op for it).
      const hingeMatch = sizeCode && bodyMatch && swingCode !== 'in'
        ? bestHingeMatch(hingeImageLayers, { size: sizeCode, color: colorCode, hardwareColor: hardwareCode, hingeSwing: hingeCode })
        : null;
      const targetHingeUrl = hingeMatch ? hingeMatch.imageUrl : null;

      const hwMatch = sizeCode && bodyMatch
        ? bestImageMatch(hardwareImageLayers, { size: sizeCode, hardwareColor: hardwareCode, lockType: lockCode, hingeSwing: hingeCode })
        : null;
      const targetHwUrl = hwMatch ? hwMatch.imageUrl : null;

      // Remembered so add-to-cart can carry the same photo through as hidden line item
      // properties (cart-products.liquid reads these to show the configured safe, not the
      // generic placeholder-variant image). Cleared when nothing matches, same as the hero.
      state.configuredImageUrl = bodyMatch ? bodyMatch.imageUrl : null;
      state.configuredHingeImageUrl = targetHingeUrl;
      state.configuredHardwareImageUrl = targetHwUrl;
      // Open-door counterpart of the matched body photo (same size + body color + trim, shot
      // once regardless of hardware/lock — see safe_image_layer.open_image_url), shown on hover
      // by initOpenImageHover() below. Resolved via its own hardware/lock-agnostic match
      // (bestOpenMatch) rather than reading bodyMatch.openImageUrl directly, so a combo whose
      // specific hw+lock row lacks an open photo still shows the one attached to a sibling row
      // for the same color+trim. Not every combo has one yet, so this can still be null.
      const openMatch = sizeCode && bodyMatch
        ? bestOpenMatch(bodyImageLayers, { size: sizeCode, color: colorCode, hingeSwing: hingeCode, requiredOption: trimCode })
        : null;
      state.configuredOpenImageUrl = openMatch ? openMatch.openImageUrl : null;
      // Cursor hint (see .rc__hero-body[data-rc-has-open] in the CSS) — only worth a pointer
      // cursor when this specific combo actually has an open-door shot to reveal.
      if (state.configuredOpenImageUrl) {
        heroEl.setAttribute('data-rc-has-open', '');
      } else {
        heroEl.removeAttribute('data-rc-has-open');
      }

      const currentHingeUrl = heroHingeEl && heroHingeEl.classList.contains('is-visible') ? heroHingeEl.src : null;
      const currentHwUrl = heroHardwareEl && heroHardwareEl.classList.contains('is-visible') ? heroHardwareEl.src : null;
      if (heroEl.src === targetBodyUrl && currentHingeUrl === targetHingeUrl && currentHwUrl === targetHwUrl) {
        return; // nothing actually changed — skip the preload/swap dance entirely
      }

      // Preload the full new layer stack (body + hinge + hardware) off-screen and swap all
      // three in one go only once every layer that's changing has finished loading, with a
      // skeleton shimmer over the frame in the meantime. Otherwise a small overlay PNG (which
      // finishes loading almost instantly) would render on top of the still-loading, much
      // larger previous-size body photo — a flash of the new hinge/hardware mismatched
      // against the old body image. The sticky add-to-cart thumbnail mirrors the same swap
      // so it's never a step behind (or a layer short of) the main hero image.
      const swapToken = ++heroSwapToken;
      if (frameEl) frameEl.classList.add('is-swapping');

      Promise.all([preloadImage(targetBodyUrl), preloadImage(targetHingeUrl), preloadImage(targetHwUrl)]).then(() => {
        if (swapToken !== heroSwapToken) return; // superseded by a newer selection mid-load

        heroEl.src = targetBodyUrl;
        heroEl.alt = bodyMatch ? data.model.name + ', configured' : heroEl.alt;

        if (heroHingeEl) {
          if (targetHingeUrl) {
            heroHingeEl.src = targetHingeUrl;
            heroHingeEl.classList.add('is-visible');
          } else {
            heroHingeEl.classList.remove('is-visible');
          }
        }

        if (heroHardwareEl) {
          if (targetHwUrl) {
            heroHardwareEl.src = targetHwUrl;
            heroHardwareEl.classList.add('is-visible');
          } else {
            heroHardwareEl.classList.remove('is-visible');
          }
        }

        if (stickyCartImageEl) stickyCartImageEl.src = targetBodyUrl;
        if (stickyCartHingeEl) {
          if (targetHingeUrl) {
            stickyCartHingeEl.src = targetHingeUrl;
            stickyCartHingeEl.classList.add('is-visible');
          } else {
            stickyCartHingeEl.classList.remove('is-visible');
          }
        }
        if (stickyCartHardwareEl) {
          if (targetHwUrl) {
            stickyCartHardwareEl.src = targetHwUrl;
            stickyCartHardwareEl.classList.add('is-visible');
          } else {
            stickyCartHardwareEl.classList.remove('is-visible');
          }
        }

        // "Your build" thumbnail (first slot in the rail) mirrors the same layer stack, so
        // the thumbnails stay a live preview of the current configuration, not just the
        // main hero. Reusing the exact same URLs the hero/sticky-cart just loaded — no
        // extra network fetch, the browser's HTTP cache already has them.
        if (thumbConfiguredBody) thumbConfiguredBody.src = targetBodyUrl;
        if (thumbConfiguredHinge) {
          if (targetHingeUrl) {
            thumbConfiguredHinge.src = targetHingeUrl;
            thumbConfiguredHinge.classList.add('is-visible');
          } else {
            thumbConfiguredHinge.classList.remove('is-visible');
          }
        }
        if (thumbConfiguredHardware) {
          if (targetHwUrl) {
            thumbConfiguredHardware.src = targetHwUrl;
            thumbConfiguredHardware.classList.add('is-visible');
          } else {
            thumbConfiguredHardware.classList.remove('is-visible');
          }
        }
        // The hero now genuinely shows the configuration again (a selection changed, or a
        // static thumbnail's clear-overlay handler ran and this recomputed a real match) —
        // reflect that in the rail's active state even if the shopper never clicked this
        // thumb directly.
        setActiveThumb(thumbConfiguredBtn);

        if (frameEl) frameEl.classList.remove('is-swapping');

        // Warm the browser cache for the open-door photo so the first hover is instant
        // instead of waiting on a fresh network fetch. Fire-and-forget — initOpenImageHover()
        // re-checks state.configuredOpenImageUrl itself when the hover actually happens.
        preloadImage(state.configuredOpenImageUrl);
      });
    }

    // Hovering the main image swaps the closed configured photo for its open-door
    // counterpart (state.configuredOpenImageUrl, set alongside the closed match in
    // updateConfiguredHero()) — only while the hero is actually showing the live
    // configuration, not a clicked static product-media thumb, and only when this size +
    // body color combo has been shot with the door open. The hardware overlay (drawn to sit
    // on the closed door) is hidden for the duration, since the open shot has no exterior
    // hardware to composite it onto.
    function initOpenImageHover() {
      if (!frameEl || !heroEl) return;
      let hovering = false;

      function showOpen() {
        if (!hovering) return; // pointer already left before the preload resolved
        if (!thumbConfiguredBtn || !thumbConfiguredBtn.classList.contains('rc__thumb--active')) return;
        if (!state.configuredOpenImageUrl) return;
        heroEl.src = state.configuredOpenImageUrl;
        heroEl.alt = data.model.name + ', open';
        if (heroHingeEl) heroHingeEl.classList.add('rc__hero-hinge--hover-hidden');
        if (heroHardwareEl) heroHardwareEl.classList.add('rc__hero-hardware--hover-hidden');
      }

      function showClosed() {
        if (heroHingeEl) heroHingeEl.classList.remove('rc__hero-hinge--hover-hidden');
        if (heroHardwareEl) heroHardwareEl.classList.remove('rc__hero-hardware--hover-hidden');
        if (!state.configuredImageUrl) return; // nothing to restore to (no match / static thumb active)
        heroEl.src = state.configuredImageUrl;
        heroEl.alt = data.model.name + ', configured';
      }

      frameEl.addEventListener('mouseenter', () => {
        hovering = true;
        preloadImage(state.configuredOpenImageUrl).then(showOpen);
      });
      frameEl.addEventListener('mouseleave', () => {
        hovering = false;
        showClosed();
      });
    }

    // Single source of truth for "which thumbnail is active" — both the configured-build
    // thumb and every static product-media thumb go through this, so exactly one is ever
    // marked active regardless of which kind of thumb triggered the change.
    function setActiveThumb(activeEl) {
      root.querySelectorAll('.rc__thumb').forEach((el) => {
        const isActive = el === activeEl;
        el.classList.toggle('rc__thumb--active', isActive);
        el.setAttribute('aria-pressed', String(isActive));
      });
    }

    // Derived summary line (e.g. Colonial's "Interior fabric: Camel/Gray Trunkliner", set by
    // the body color once an accent is also chosen) and included-accessory lines (e.g. the
    // Colonial 50's Rifle Rod Kit, included only in Standard trim). Both are data-driven via
    // option_value.dependencies — no-ops when that data isn't present, so Franklin is unaffected.
    function updateDerivedAndIncludedLines() {
      if (derivedEl) {
        // First match wins, in step order — e.g. Lincoln's trim (Premium Signature's fixed
        // Diamond PVC interior) must override accent/body's derived velour color entirely,
        // not just be the last one checked. Step order already puts trim before accent
        // before body, so "first match, then stop" gives the right priority for free.
        let derivedText = '';
        for (const g of otherGroups) {
          if (!isGroupVisible(g)) continue;
          const code = state.selections[g.handle];
          const value = code ? findValueByCode(g.id, code) : null;
          const derived = value && value.dependencies && value.dependencies.derived_label;
          if (derived && derived.key && derived.value) {
            derivedText = derived.key + ': ' + derived.value;
            break;
          }
        }
        derivedEl.textContent = derivedText;
        derivedEl.hidden = !derivedText;
      }

      if (includedEl) {
        const includedCodes = (state.sizeConfig && state.sizeConfig.includedOptionCodes) || [];
        const activeLabels = [];
        for (const code of includedCodes) {
          const value = data.optionValues.find((v) => v.code === code);
          if (!value) continue;
          const requires = value.dependencies && value.dependencies.requires;
          const matches = !requires || Object.keys(requires).every((handle) => effectiveSelection(handle) === requires[handle]);
          if (matches) activeLabels.push(value.label);
        }
        includedEl.textContent = activeLabels.length ? 'Included: ' + activeLabels.join(', ') : '';
        includedEl.hidden = activeLabels.length === 0;
      }
    }

    function render() {
      // Size step
      stepsEl.querySelectorAll('[data-rc-size]').forEach((el) => {
        el.setAttribute('aria-pressed', String(Boolean(state.sizeConfig && String(state.sizeConfig.id) === el.dataset.rcSize)));
      });
      const sizeLabelEl = stepsEl.querySelector('[data-rc-size-label]');
      if (sizeLabelEl) sizeLabelEl.textContent = state.sizeConfig ? state.sizeConfig.name : 'Select a size';
      updateSizeCapacities();
      updateKeyFeatures();

      // Conditional steps (e.g. Paint Style) toggle in/out of the DOM entirely here — a
      // hidden step is skipped for rendering, pricing, and the missing-selections count below,
      // exactly as if it didn't exist on this model at all.
      otherGroups.forEach((g) => {
        const step = stepsEl.querySelector('[data-rc-group-handle="' + g.handle + '"]');
        const visible = isGroupVisible(g);
        if (step) step.hidden = !visible;
        if (visible) renderOptionsFor(g);
      });
      updateDerivedAndIncludedLines();
      updateConfiguredHero();

      // Price + summary
      let total = state.sizeConfig ? Number(state.sizeConfig.basePrice) : 0;
      const parts = [state.sizeConfig ? state.sizeConfig.name : data.model.name];
      const missing = [];
      if (!state.sizeConfig) missing.push('a size');

      otherGroups.forEach((g) => {
        if (!isGroupVisible(g)) return;
        const code = state.selections[g.handle];
        const value = code ? findValueByCode(g.id, code) : null;
        if (value) {
          total += effectiveModifier(value);
          parts.push(value.label);
        } else {
          missing.push((g.titleName || g.displayName).toLowerCase());
          parts.push(g.displayName);
        }
      });

      if (priceTopEl) priceTopEl.textContent = money(total);
      if (priceTotalEl) priceTotalEl.textContent = money(total);
      if (priceNoteEl) {
        priceNoteEl.textContent = missing.length ? 'Estimated with the options selected so far' : 'Configured price, before tax and delivery';
      }
      if (configStringEl) configStringEl.textContent = parts.join(' / ');

      if (ctaEl) {
        ctaEl.disabled = missing.length > 0;
        ctaEl.textContent = missing.length ? 'Select ' + missing[0] + ' to continue' : 'Add to Cart - ' + money(total);
      }

      // Sticky cart bar mirrors the real CTA exactly — same disabled state and
      // price, so there's never a version where the two disagree.
      if (stickyCartPriceEl) stickyCartPriceEl.textContent = money(total);
      if (stickyCartVariantEl) {
        stickyCartVariantEl.textContent = parts.length > 1 ? parts.slice(1).join(' / ') : 'Choose your configuration';
      }
      if (stickyCartAddEl) stickyCartAddEl.disabled = missing.length > 0;
      if (stickyCartAddLabelEl) {
        stickyCartAddLabelEl.textContent = missing.length ? 'Select ' + missing[0] : 'Add to Cart - ' + money(total);
      }

      if (ctaHelperEl) {
        ctaHelperEl.textContent = missing.length ? 'Still needed: ' + missing.join(', ') + '.' : '';
      }
    }

    // Restores a previously-saved configuration when arriving from the cart's "Edit
    // configuration" button (see configurator-cart-line.js), which passes the saved
    // selection/session as query params instead of leaving the shopper to start over blank.
    // No-op (silently falls through to the normal blank-start state) if the params are
    // missing, the selection can't be found, or it doesn't match this model's sizes.
    async function restoreFromQueryParams() {
      const params = new URLSearchParams(window.location.search);

      // Simple size preselect from the collection page's "Choose from N Sizes" card — no
      // draft-order/session round-trip needed, just points state.sizeConfig at the chosen
      // size so the shopper lands mid-flow instead of starting the configurator blank.
      const preselectSize = params.get('size');
      if (preselectSize) {
        const config = sizeConfigs.find((c) => c.sizeCode === preselectSize);
        if (config) state.sizeConfig = config;
        params.delete('size');
        history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
      }

      const selectionId = params.get('restore_selection');
      const sessionToken = params.get('restore_token');
      if (!selectionId || !sessionToken) return;

      try {
        const response = await fetch(data.apiBase + '/configurator-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ selectionId, sessionToken }),
        });
        if (!response.ok) return;
        const result = await response.json();
        if (!result.found || !result.selections) return;

        const saved = result.selections;
        if (saved.size) {
          const config = sizeConfigs.find((c) => c.sizeCode === saved.size);
          if (config) state.sizeConfig = config;
        }
        otherGroups.forEach((g) => {
          if (saved[g.handle]) state.selections[g.handle] = saved[g.handle];
        });
      } catch (error) {
        console.warn('Rhino configurator: failed to restore saved selection', error);
      } finally {
        // Clean the URL regardless of outcome so a refresh/share doesn't keep re-triggering
        // this (or, on failure, keep pointing at a now-stale/consumed selection).
        params.delete('restore_selection');
        params.delete('restore_token');
        const query = params.toString();
        history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
      }
    }

    // Seeds a real starting configuration (size + every other group's first/cheapest
    // option, or its explicit is_default if one exists) so the shopper lands on a
    // complete, orderable configuration with something already selected — same
    // mechanism as product-configurator-amsec.js. Seeded before
    // restoreFromQueryParams() runs so a real saved selection still wins.
    function applyDefaultSelections() {
      if (!state.sizeConfig && sizeConfigs.length) {
        // Always the smallest size for this model. Prefer weightLbs (a direct physical
        // measure) when present (Colonial); fall back to basePrice when it isn't
        // (Franklin/Lincoln/Presidential have no weight_lbs data at all yet). Price
        // alone isn't fully reliable in general — AMSEC's BF line has a base_price
        // anomaly where a smaller size costs more than a larger one — so weight wins
        // the tie whenever both sizes have it. Deliberately ignores the shared size
        // option_value's own isDefault flag: that value is shared across every series
        // on this line (Franklin, Colonial, Lincoln, ...), so it can only ever mark one
        // series' preferred size and is wrong for every other one.
        const sizeMetric = (c) => (c.weightLbs != null ? Number(c.weightLbs) : Number(c.basePrice));
        const smallest = sizeConfigs.slice().sort((a, b) => sizeMetric(a) - sizeMetric(b))[0];
        if (smallest) state.sizeConfig = smallest;
      }
      otherGroups.forEach((g) => {
        // Must scope to this size's availableOptionCodes, same as effectiveSelection()
        // and buildOptionStep() do — otherwise a shared group with more than one
        // isDefault:true value (one per series) picks whichever flagged value sorts
        // first group-wide (cheapest first) instead of the one actually valid for
        // this series' size, even when that value isn't offered on this series at all.
        const defaultValue = defaultValueForGroup(g, allowedCodesForGroup(g));
        if (defaultValue) state.selections[g.handle] = defaultValue.code;
      });
    }

    function buildAll() {
      // A model with only one size (e.g. Classic Select) has nothing to choose between —
      // applyDefaultSelections() below still auto-selects that one size into state.sizeConfig,
      // so skip rendering a "pick one of one" step instead of showing a pointless single-card grid.
      if (sizeConfigs.length > 1) buildSizeStep();
      otherGroups.forEach((g) => buildOptionStep(g));
      applyDefaultSelections();
      buildAutoGalleryThumbs();
      restoreFromQueryParams().finally(render);
    }

    // Sticky cart bar — same show/hide-on-scroll technique as
    // safe-builder.liquid's #sticky-cart (IntersectionObserver on the real
    // CTA), so this reads as the same feature across every PDP on the site.
    function initStickyCart() {
      if (!stickyCartEl || !ctaEl) return;

      // The jump button's visibility depends on TWO independent signals —
      // whether the real CTA is off-screen (the observer below) AND whether
      // the mobile card is collapsed (the toggle below) — funneled through
      // one function so there's a single place setting its inline style.
      // Setting it from the observer alone fights with the CSS collapsed-
      // state display:none rule (inline always wins over a class-based rule
      // regardless of specificity): once the CTA scrolled out of view the
      // button stayed visible even while "collapsed", sitting in a grid area
      // the collapsed 1-row layout doesn't have — scrambling the price/
      // image/button positions.
      let ctaOffScreen = false;
      function updateJumpVisibility() {
        if (!stickyCartJumpEl) return;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const collapsedOnMobile = isMobile && !stickyCartEl.classList.contains('expanded');
        stickyCartJumpEl.style.display = (ctaOffScreen && !collapsedOnMobile) ? 'flex' : 'none';
      }

      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // isIntersecting alone can't tell "scrolled past it" (top < 0)
              // from "haven't scrolled down to it yet" (still below the
              // viewport) — both report false. On a tall hero the real CTA
              // starts below the fold, so without this check the sticky bar
              // showed immediately at the top of the page instead of only
              // once the shopper actually scrolls past the real button.
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              stickyCartEl.classList.toggle('visible', scrolledPast);
              ctaOffScreen = scrolledPast;
              updateJumpVisibility();
            });
          },
          { threshold: 0.1 }
        );
        observer.observe(ctaEl);
      }

      window.addEventListener('resize', updateJumpVisibility, { passive: true });

      if (stickyCartAddEl) {
        stickyCartAddEl.addEventListener('click', () => {
          if (!ctaEl.disabled) ctaEl.click();
        });
      }

      if (stickyCartJumpEl) {
        stickyCartJumpEl.addEventListener('click', () => {
          ctaEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        });
      }

      // Mobile collapse/expand handle — desktop's floating pill is small
      // enough already and hides this button entirely via CSS, so this is a
      // no-op there regardless of state.
      if (stickyCartToggleEl) {
        stickyCartToggleEl.addEventListener('click', () => {
          const expanded = stickyCartEl.classList.toggle('expanded');
          stickyCartToggleEl.setAttribute('aria-expanded', String(expanded));
          stickyCartToggleEl.setAttribute('aria-label', expanded ? 'Hide product details' : 'Show product details');
          updateJumpVisibility();
        });
      }
    }

    stepsEl.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;

      if (btn.dataset.rcSize) {
        const config = sizeConfigs.find((c) => String(c.id) === btn.dataset.rcSize);
        if (!config) return;
        state.sizeConfig = config;
        // Clear any selection no longer valid for this size, then re-default anything that
        // just got cleared (or never had a selection) — otherwise switching to a size whose
        // option set genuinely differs from the previous one (e.g. Centurion: handle_style
        // only exists on 24/32, and its real hardware/color choices differ by size; Presidential:
        // several body colors and Chrome hardware simply have no photography on size 40) leaves
        // those steps blank, or worse, silently stale, instead of landing on a real starting
        // configuration, the same way the very first page load does via
        // applyDefaultSelections(). No-op for any model whose groups stay valid across every
        // size (every model before Centurion).
        //
        // Single pass in sortOrder (otherGroups is already sorted), not two separate passes —
        // dependencyAllowList(g) only ever consults EARLIER-sortOrder groups, so processing
        // groups in order guarantees every earlier group has already been finalized (kept or
        // freshly re-defaulted) before a later group's validity/default is computed against it.
        // A flat two-pass version (clear everything using only the size's flat available_options,
        // then re-default only what got cleared) can leave a LATER group — e.g. body_color —
        // flatly "still valid" for the new size while no longer compatible with whatever the
        // EARLIER group — e.g. accent_color/hardware — just landed on, since the flat size list
        // has no notion of cross-group pairing. Folding dependencyAllowList into both the
        // validity check and the default's candidate list closes that gap.
        otherGroups.forEach((g) => {
          const code = state.selections[g.handle];
          const sizeAllowed = allowedCodesForGroup(g);
          const depAllowed = dependencyAllowList(g);
          const stillValid = code
            && (!sizeAllowed || sizeAllowed.indexOf(code) !== -1)
            && (!depAllowed || depAllowed.indexOf(code) !== -1);
          if (code && !stillValid) {
            delete state.selections[g.handle];
          }
          if (!state.selections[g.handle]) {
            const allowed = depAllowed && sizeAllowed
              ? sizeAllowed.filter((c) => depAllowed.indexOf(c) !== -1)
              : (depAllowed || sizeAllowed);
            const defaultValue = defaultValueForGroup(g, allowed);
            if (defaultValue) state.selections[g.handle] = defaultValue.code;
          }
        });
        state.clearedNotice = null;
        return render();
      }

      const step = btn.closest('[data-rc-group-handle]');
      if (step && btn.dataset.rcValue) {
        const handle = step.dataset.rcGroupHandle;
        const group = otherGroups.find((g) => g.handle === handle);
        const nextCode = btn.dataset.rcValue;
        state.selections[handle] = nextCode;
        state.clearedNotice = null;

        // If this selection filters another group (e.g. accent -> body), auto-swap any
        // now-invalid downstream selection to that group's first valid option (same
        // no-cost/cheapest-first order as every other default) instead of clearing it —
        // clearing left the hero showing the generic fallback product photo until the
        // shopper picked something new, which read as a broken image on every hardware
        // change that invalidated the current color.
        const nextValue = findValueByCode(group.id, nextCode);
        const nextValueSizeScoped = nextValue && nextValue.dependencies && nextValue.dependencies.allowed_values_by_size;
        const nextValueAllowedValues =
          (nextValueSizeScoped && state.sizeConfig && nextValueSizeScoped[state.sizeConfig.sizeCode]) ||
          (nextValue && nextValue.dependencies && nextValue.dependencies.allowed_values);
        if (nextValueAllowedValues) {
          const allowedValues = nextValueAllowedValues;
          // Resolve targets in group sortOrder (accent_color before body_color), not raw JSON
          // key order — dependencyAllowList's combo_hardware inverse-lookup for body_color reads
          // the LIVE state.selections.accent_color, so if body_color were resolved first it would
          // see the stale pre-click accent value (e.g. still Black Chrome right after switching to
          // 3-Spoke), intersect against the wrong combo pairing, come back empty, and wrongly
          // delete body_color entirely instead of leaving it on its still-valid value. Sorting by
          // sortOrder guarantees every EARLIER group (which is all dependencyAllowList ever
          // consults, see above) has already settled on its final value before a LATER group's
          // list is computed.
          const targetHandles = Object.keys(allowedValues).sort((a, b) => {
            const ga = otherGroups.find((g) => g.handle === a);
            const gb = otherGroups.find((g) => g.handle === b);
            return (ga ? ga.sortOrder : 999) - (gb ? gb.sortOrder : 999);
          });
          targetHandles.forEach((targetHandle) => {
            const targetGroup = otherGroups.find((g) => g.handle === targetHandle);
            if (!targetGroup) return;
            // Prefer the fully-intersected dependencyAllowList (state.selections[handle] was
            // already updated above, so this reflects it) over the clicked value's own flat
            // list — a flat list alone can be too permissive when a THIRD selection narrows the
            // target further still: accent_color's own body_color list must include both Black
            // and White since Black Chrome hardware is genuinely valid under either handle, but
            // dependencyAllowList also folds in the current handle's combo_hardware inverse to
            // correctly exclude Black once 3-Spoke specifically is active. Falls back to the
            // flat list on the rare chance dependencyAllowList finds nothing (defensive only —
            // the group whose click triggered this always contributes to its own computation).
            const allowedForTarget = dependencyAllowList(targetGroup) || allowedValues[targetHandle];
            const targetCode = state.selections[targetHandle];
            if (targetCode && allowedForTarget.indexOf(targetCode) === -1) {
              const replacement = defaultValueForGroup(targetGroup, allowedForTarget);
              if (replacement) {
                state.selections[targetHandle] = replacement.code;
              } else {
                delete state.selections[targetHandle];
                state.clearedNotice = targetHandle;
              }
            }
          });
        }

        // A color and hardware finish can each be individually "allowed" under the current
        // handle/trim selection without their PAIR being a real combo — e.g. on Centurion,
        // Black body_color and Black Chrome hardware are both valid under 3-Spoke handle on
        // their own, but the real 3-Spoke pairing for Black uses Chrome hardware (Black Chrome
        // is only real with Black under Drop handle, or with White under 3-Spoke). The plain
        // per-group allowed_values lists above can't express a pairing that depends on a THIRD
        // selection like this, so a value can instead carry dependencies.combo_hardware =
        // { [bodyColorCode]: exactAccentColorCode } to pin the exact hardware for a specific
        // color once both handle and color are known. Runs after handle_style or body_color
        // clicks so either one auto-corrects the other to a real pairing — but never after an
        // accent_color click itself, or picking Black Chrome hardware directly (to get to the
        // White combo) would get immediately overwritten back to whatever the still-stale
        // body_color's own pin says, fighting the shopper's own click. No-op for any value
        // without combo_hardware (every model, and every Centurion group other than
        // handle_style, today).
        const handleGroup = otherGroups.find((g) => g.handle === 'handle_style');
        const handleValue = handleGroup && state.selections.handle_style && findValueByCode(handleGroup.id, state.selections.handle_style);
        const comboHardware = handle !== 'accent_color' && handleValue && handleValue.dependencies && handleValue.dependencies.combo_hardware;
        const pinnedHardware = comboHardware && state.selections.body_color && comboHardware[state.selections.body_color];
        if (pinnedHardware) {
          const accentGroup = otherGroups.find((g) => g.handle === 'accent_color');
          const pinnedValue = accentGroup && findValueByCode(accentGroup.id, pinnedHardware);
          if (pinnedValue) state.selections.accent_color = pinnedValue.code;
        }

        // Switching a paint-style-like axis re-defaults body_color when the carried-over
        // selection is no longer valid — its two pools (e.g. Lincoln's 15 1-Tone colors vs 4
        // 2-Tone colorways, Presidential's same split) are disjoint value sets. Lands on the
        // first valid color in the new pool (same no-cost/cheapest-first order as every other
        // default) instead of leaving the step blank — a shopper switching paint style either
        // direction should see a real configured color immediately, not an empty picker. No-op
        // for any model without a "paint_style" group, and a no-op if the earlier accent-driven
        // auto-swap above (line ~1220) already landed body_color on something valid for the new
        // pool.
        //
        // dependencyAllowList alone isn't enough: unlike PRESIDENTIAL_2TONE/LINCOLN_2TONE (which
        // declare dependencies.allowed_values.body_color), the 1-Tone paint_style value carries
        // no such list, so once accent_color's own allowed list has been widened to cover both
        // pools (needed so a 2-Tone color can show at all), dependencyAllowList sees no
        // restriction and would call a stale 2-Tone selection "still valid" after switching back
        // to 1-Tone. The real pool split lives on each COLOR's own dependencies.requires clause
        // instead (the same one the renderer's values.filter checks above) — fold that in too.
        if (handle === 'paint_style') {
          const bodyGroup = otherGroups.find((g) => g.handle === 'body_color');
          if (bodyGroup) {
            const bodyAllowed = dependencyAllowList(bodyGroup);
            const passesRequires = (v) => {
              const requires = v && v.dependencies && v.dependencies.requires;
              return !requires || Object.keys(requires).every((h) => effectiveSelection(h) === requires[h]);
            };
            const currentBodyCode = state.selections.body_color;
            const currentBodyValue = currentBodyCode && findValueByCode(bodyGroup.id, currentBodyCode);
            const stillValid = currentBodyValue
              && passesRequires(currentBodyValue)
              && (!bodyAllowed || bodyAllowed.indexOf(currentBodyCode) !== -1);
            if (!stillValid) {
              const requiresFilteredCodes = (valuesByGroup.get(bodyGroup.id) || [])
                .filter(passesRequires)
                .map((v) => v.code);
              const allowed = bodyAllowed
                ? bodyAllowed.filter((c) => requiresFilteredCodes.indexOf(c) !== -1)
                : requiresFilteredCodes;
              const replacement = defaultValueForGroup(bodyGroup, allowed);
              if (replacement) {
                state.selections.body_color = replacement.code;
              } else {
                delete state.selections.body_color;
              }
            }
          }
        }

        // A conditional step just going out of scope (e.g. trim changing away from Premium
        // Signature hides Paint Style) leaves its old selection stale — reset it to its
        // default value the same way it started, and re-run the allowed_values cascade for
        // that default so anything it used to lock (e.g. 2-Tone forcing a Black accent) is
        // released too. No-op for models with no visibleWhen-gated groups.
        otherGroups.forEach((otherGroup) => {
          if (otherGroup.handle === handle || isGroupVisible(otherGroup)) return;
          const currentCode = state.selections[otherGroup.handle];
          const defaultValue = defaultValueForGroup(otherGroup);
          if (!currentCode || !defaultValue || currentCode === defaultValue.code) return;
          state.selections[otherGroup.handle] = defaultValue.code;
          if (otherGroup.handle === 'paint_style') delete state.selections.body_color;
          if (defaultValue.dependencies && defaultValue.dependencies.allowed_values) {
            Object.keys(defaultValue.dependencies.allowed_values).forEach((targetHandle) => {
              const targetCode = state.selections[targetHandle];
              if (targetCode && defaultValue.dependencies.allowed_values[targetHandle].indexOf(targetCode) === -1) {
                delete state.selections[targetHandle];
              }
            });
          }
        });

        // A single VALUE can go out of scope without its whole group doing so — e.g.
        // Franklin's Dormakaba lock only exists under Premium Signature trim
        // (dependencies.requires), but the Lock step itself stays visible under every
        // trim (unlike Paint Style above, which disappears entirely). The
        // renderOptionsFor() filter already hides such a value from the picker, but
        // without this, a currently-SELECTED value that just became hidden stays
        // selected in state with nothing rendered as chosen. Re-check every other
        // group's current selection against its own value's requires clause after any
        // click, and fall back to that group's default (already sorted cheapest-first
        // — same rule as every other default pick, so this lands on the cheaper lock,
        // not an arbitrary one) rather than leaving a hidden value selected.
        otherGroups.forEach((otherGroup) => {
          if (otherGroup.handle === handle) return;
          const currentCode = state.selections[otherGroup.handle];
          if (!currentCode) return;
          const currentValue = findValueByCode(otherGroup.id, currentCode);
          const requires = currentValue && currentValue.dependencies && currentValue.dependencies.requires;
          if (!requires) return;
          const stillValid = Object.keys(requires).every((h) => effectiveSelection(h) === requires[h]);
          if (stillValid) return;

          const passesRequires = (v) => {
            const req = v && v.dependencies && v.dependencies.requires;
            return !req || Object.keys(req).every((h) => effectiveSelection(h) === req[h]);
          };
          const sizeAllowed = allowedCodesForGroup(otherGroup);
          const depAllowed = dependencyAllowList(otherGroup);
          const requiresFilteredCodes = (valuesByGroup.get(otherGroup.id) || [])
            .filter(passesRequires)
            .map((v) => v.code);
          let allowed = depAllowed && sizeAllowed
            ? sizeAllowed.filter((c) => depAllowed.indexOf(c) !== -1)
            : (depAllowed || sizeAllowed);
          allowed = allowed ? allowed.filter((c) => requiresFilteredCodes.indexOf(c) !== -1) : requiresFilteredCodes;
          const replacement = defaultValueForGroup(otherGroup, allowed);
          if (replacement) {
            state.selections[otherGroup.handle] = replacement.code;
          } else {
            delete state.selections[otherGroup.handle];
          }
        });

        return render();
      }
    });

    // Attached unconditionally (not gated on any [data-rc-photo] existing yet) — the auto-gallery
    // thumbs are built later, inside buildAll(), so nothing matches at bind time; delegation means
    // that's fine, it just never fires until they exist.
    root.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-rc-photo]');
      if (!thumb) return;
      if (heroEl) {
        heroEl.src = thumb.dataset.rcPhoto;
        heroEl.alt = thumb.dataset.rcPhotoAlt || '';
      }
      // Most Rhino models' auto-gallery thumbs are a single flat body photo (hardware baked
      // in), so clicking one clears the hero's overlays same as before. A model like Colonial —
      // whose body photo has no hardware/logo baked in — instead has a matching hinge/hardware
      // overlay attached to its thumb (see buildAutoGalleryThumbs), which gets composited onto
      // the hero here instead of just being cleared.
      const hingeUrl = thumb.dataset.rcPhotoHinge || null;
      const hwUrl = thumb.dataset.rcPhotoHardware || null;
      if (heroHingeEl) {
        if (hingeUrl) {
          heroHingeEl.src = hingeUrl;
          heroHingeEl.classList.add('is-visible');
        } else {
          heroHingeEl.classList.remove('is-visible');
        }
      }
      if (heroHardwareEl) {
        if (hwUrl) {
          heroHardwareEl.src = hwUrl;
          heroHardwareEl.classList.add('is-visible');
        } else {
          heroHardwareEl.classList.remove('is-visible');
        }
      }
      heroEl.removeAttribute('data-rc-has-open');
      state.configuredImageUrl = null;
      state.configuredHingeImageUrl = null;
      state.configuredHardwareImageUrl = null;
      state.configuredOpenImageUrl = null;
      setActiveThumb(thumb);
    });

    // "Your build" thumbnail — clicking it hands the hero back to the live configuration
    // (re-running the same match/preload/swap updateConfiguredHero() already does), which
    // matters once a static product-media thumb above has taken over the hero. Its own
    // is-swapping/active-state bookkeeping happens inside updateConfiguredHero() itself, the
    // same as any other selection change, so this click just needs to trigger that.
    if (thumbConfiguredBtn) {
      thumbConfiguredBtn.addEventListener('click', () => {
        setActiveThumb(thumbConfiguredBtn);
        updateConfiguredHero();
      });
    }

    // Main-image prev/next arrows — advance through the same [data-rc-photo]
    // thumbnails the rail already handles, by dispatching a real click at the
    // target thumb so the existing delegated handler above (src swap +
    // clearing the configured hardware overlay) runs unchanged.
    (function initFrameArrows() {
      const prevBtn = root.querySelector('[data-rc-frame-prev]');
      const nextBtn = root.querySelector('[data-rc-frame-next]');
      if (!prevBtn || !nextBtn) return;

      // Re-queried on every click rather than captured once here — the auto-gallery thumbs
      // are built later, inside buildAll(), so a one-time snapshot taken at bind time would
      // permanently be empty.
      function clickRelative(direction) {
        const thumbs = Array.from(root.querySelectorAll('[data-rc-photo]'));
        if (!thumbs.length) return;
        const activeIndex = thumbs.findIndex((t) => t.classList.contains('rc__thumb--active'));
        const from = activeIndex === -1 ? 0 : activeIndex;
        const nextIndex = (from + direction + thumbs.length) % thumbs.length;
        thumbs[nextIndex].click();
      }

      prevBtn.addEventListener('click', () => clickRelative(-1));
      nextBtn.addEventListener('click', () => clickRelative(1));
    })();

    // Mobile-only nav arrows for the thumbnail rail (a vertical column on
    // desktop, a horizontally-scrollable row on mobile — see the CSS). Purely
    // a scroll aid alongside swipe; doesn't change which photo is selected.
    (function initThumbNavArrows() {
      const thumbs = root.querySelector('.rc__thumbs');
      const prevBtn = root.querySelector('[data-rc-thumbs-prev]');
      const nextBtn = root.querySelector('[data-rc-thumbs-next]');
      if (!thumbs || !prevBtn || !nextBtn) return;

      function scrollThumbs(direction) {
        const firstThumb = thumbs.querySelector('.rc__thumb');
        const gap = 10;
        const step = firstThumb ? firstThumb.getBoundingClientRect().width + gap : 74;
        thumbs.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      prevBtn.addEventListener('click', () => scrollThumbs(-1));
      nextBtn.addEventListener('click', () => scrollThumbs(1));
    })();

    async function saveSelectionOnBackend() {
      const selections = { size: state.sizeConfig.sizeCode };
      otherGroups.forEach((g) => {
        if (!isGroupVisible(g)) return; // e.g. a stale paint_style pick after leaving Premium
        selections[g.handle] = state.selections[g.handle];
      });

      const response = await fetch(data.apiBase + '/configurator-save-selection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: String(data.productId),
          shopDomain: data.shopDomain,
          selections: JSON.stringify(selections),
          modelId: data.modelId != null ? String(data.modelId) : null,
        }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || response.statusText);
      }
      const result = await response.json();
      if (!result.selectionId) throw new Error(result.error || 'Failed to save configuration');
      return result;
    }

    async function addConfiguredLineToCart(saved) {
      // Visible properties (Shopify's cart template hides any key starting with "_"), built
      // generically from group display names so this works for any brand/model, not just
      // Franklin. Hidden properties carry what configurator-cart-line.js needs to find this
      // line again and re-price/checkout it correctly.
      const properties = { Size: state.sizeConfig.name };
      otherGroups.forEach((g) => {
        if (!isGroupVisible(g)) return;
        const code = state.selections[g.handle];
        const value = code ? findValueByCode(g.id, code) : null;
        if (value) properties[g.displayName] = value.label;
      });
      properties._configurator = 'true';
      properties._configurationId = saved.selectionId;
      properties._sessionToken = saved.sessionToken;
      // Carries the exact configured photo into the cart (see updateConfiguredHero). Omitted
      // when this size/color/hardware/lock combo hasn't been photographed — cart-products.liquid
      // falls back to the normal placeholder-variant image in that case.
      if (state.configuredImageUrl) properties._configuredImageUrl = state.configuredImageUrl;
      if (state.configuredHingeImageUrl) properties._configuredHingeImageUrl = state.configuredHingeImageUrl;
      if (state.configuredHardwareImageUrl) properties._configuredHardwareImageUrl = state.configuredHardwareImageUrl;

      // Reconfiguring THIS product (e.g. re-submitting after the cart's "Edit configuration"
      // link, or double-clicking Add to Cart) must replace this product's own previously-added
      // configured line, not sit alongside it as a second cart item for the same safe. Matched
      // on this product's placeholder variant specifically — a configured line for a *different*
      // product/model must be left alone so multiple configured safes can coexist in the cart.
      const existingCart = await fetch('/cart.js').then((r) => r.json());
      const existingConfiguredLine = (existingCart.items || []).find(
        (item) =>
          item.properties &&
          item.properties._configurator === 'true' &&
          String(item.variant_id) === String(data.variantId)
      );
      if (existingConfiguredLine) {
        // Tell the backend this old selection is dead before dropping its cart line — mirrors
        // wireEditConfiguration() in configurator-cart-line.js. Without this, a selection that
        // already reached "checkoutCreated" (shopper started checkout, then came back and
        // reconfigured instead of finishing) is left orphaned: still "checkoutCreated" with an
        // open draft order, even though its cart line is gone. If it (or a stray duplicate line
        // for it) ever ends up back in a checkout request, createCheckoutDraftOrder.ts rejects
        // the whole checkout with "Selection is checkoutCreated and can no longer be checked
        // out" — this happened in production, see conversation. Best-effort: a failure here
        // shouldn't block adding the new configuration.
        const oldProps = existingConfiguredLine.properties || {};
        if (oldProps._configurationId && oldProps._sessionToken) {
          try {
            await fetch(data.apiBase + '/configurator-abandon', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                selectionId: oldProps._configurationId,
                sessionToken: oldProps._sessionToken,
              }),
            });
          } catch (error) {
            console.warn('Rhino configurator: failed to abandon replaced selection', error);
          }
        }

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingConfiguredLine.key, quantity: 0 }),
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: data.variantId, quantity: 1, properties }],
        }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || response.statusText);
      }
    }

    if (ctaEl) {
      ctaEl.addEventListener('click', async () => {
        if (ctaEl.disabled) return;
        const originalText = ctaEl.textContent;
        ctaEl.disabled = true;
        ctaEl.textContent = 'Adding...';
        try {
          const saved = await saveSelectionOnBackend();
          await addConfiguredLineToCart(saved);
          window.location.href = '/cart';
        } catch (err) {
          console.error('Rhino configurator add-to-cart error:', err);
          alert('Sorry, there was a problem adding your configuration to the cart. Please try again.');
          ctaEl.disabled = false;
          ctaEl.textContent = originalText;
        }
      });
    }

    buildAll();
    initStickyCart();
    initOpenImageHover();
  }

  document.querySelectorAll('.rc').forEach(init);
})();
